import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { resolveHref } from '../utils/resolveHref';
import { useCtaColorVars } from '../hooks/useCtaColorVars';
import { onColorLinksCss } from '../utils/onColorLinks';

// ============================================================================
// Dedicated closing section for the Better-Built Living page: "Redefining
// Modern Living". Visually matches LifestyleClosingCTA (full-bleed primary
// band with optional photo + overlay, centered copy and CTA) but ships with
// Better-Built-specific defaults so the Better-Built page doesn't share its
// closing copy with /the-lifesyle.
// ============================================================================

const backgroundDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      defaultValue: 'Community exterior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a wide community-wide gallery image — exterior, aerial, courtyard, pool deck, or clubroom — that sells the overall sense of place. Skip bedrooms, bathrooms, closets, and floor-plan diagrams.',
    context:
      'Full-bleed background behind the closing Better-Built Living CTA, under a heavy primary-color overlay. Should feel broad and inviting, not a close-up of a single unit.',
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://zmodular.com/manufacturing',
    },
    label: {
      label: 'Label',
      type: 'text',
      defaultValue: 'Visit Our Manufacturing Page',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

export function BetterBuiltClosing() {
  useRevealStyles();

  const background = useSlotGroup('betterbuiltclosing.background', backgroundDef);
  const cta = useSlotGroup('betterbuiltclosing.cta', ctaDef);
  const contentRef = useScrollReveal<HTMLDivElement>();
  const ctaColorVars = useCtaColorVars();

  return (
    <section className="bbcl-section" style={ctaColorVars}>
      {/* Background photo + heavy primary overlay. */}
      <div className="bbcl-bg">
        <EditableSlotGroup
          groupPath="betterbuiltclosing.background"
          definition={backgroundDef}
        >
          <div className="absolute inset-0">
            {background.image ? (
              <VibesitesImage
                url={background.image}
                alt={background.alt || ''}
                sizes="100vw"
                className="absolute inset-0 h-full w-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-primary)]" />
            )}
            <div className="absolute inset-0 bg-[var(--vibe-color-primary)] opacity-[0.85]" />
          </div>
        </EditableSlotGroup>
      </div>

      <div ref={contentRef} className="reveal-fade-up bbcl-container">
        <RichTextContent
          path="betterbuiltclosing.heading"
          label="Heading"
          defaultValue="<p>Redefining Modern Living</p>"
          as="h2"
          className="bbcl-heading"
        />
        <RichTextContent
          path="betterbuiltclosing.subheading"
          label="Body"
          defaultValue='<p>At <span data-type="field-token" data-field="property_name"></span>, we&rsquo;re combining innovation, sustainability, and thoughtful design to transform the way you live. More than just a home, it&rsquo;s a space crafted to enhance your lifestyle. That&rsquo;s what we call &ldquo;Better-Built Living&rdquo;.</p><p>For more information on how we build, visit our manufacturing page.</p>'
          as="div"
          className="bbcl-sub"
        />

        <EditableSlotGroup groupPath="betterbuiltclosing.cta" definition={ctaDef}>
          <a
            href={resolveHref(cta.url || 'https://zmodular.com/manufacturing')}
            target={cta._target || '_blank'}
            rel={cta._target === '_blank' ? 'noopener noreferrer' : undefined}
            className="bbcl-cta"
          >
            {cta.label || 'Visit Our Manufacturing Page'}
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        ${onColorLinksCss('.bbcl-section [data-vibesite-richtext]')}
        .bbcl-section {
          position: relative;
          overflow: hidden;
          background: var(--vibe-color-primary);
          padding: 6rem 1.5rem 6rem;
        }
        .bbcl-bg {
          position: absolute;
          inset: 0;
        }

        .bbcl-container {
          position: relative;
          z-index: 1;
          max-width: 960px;
          margin: 0 auto;
          text-align: center;
          color: #ffffff;
        }

        .bbcl-heading,
        .bbcl-heading * {
          font-family: var(--vibe-font-heading);
          font-weight: 400;
        }
        .bbcl-heading {
          font-size: clamp(2.1rem, 5vw, 3.5rem);
          line-height: 1.05;
          letter-spacing: 0.03em;
          margin: 0 auto 1.25rem;
          max-width: 32rem;
        }

        .bbcl-sub,
        .bbcl-sub * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .bbcl-sub {
          color: rgba(255, 255, 255, 0.88);
          font-size: 1rem;
          line-height: 1.7;
          max-width: 56rem;
          margin: 0 auto 2.5rem;
        }
        .bbcl-sub p { margin: 0 0 0.75rem; }
        .bbcl-sub p:last-child { margin-bottom: 0; }

        .bbcl-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 1.1rem 2.75rem;
          background: var(--cta-bg, var(--vibe-color-secondary));
          color: var(--cta-text, var(--vibe-color-text-on-secondary, #ffffff));
          border: 1.5px solid var(--cta-bg, var(--vibe-color-secondary));
          font-family: var(--vibe-font-heading);
          font-size: 0.9rem;
          font-weight: 400;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          text-decoration: none;
          transition: background-color 0.25s ease, color 0.25s ease,
                      transform 0.25s ease, border-color 0.25s ease;
        }
        .bbcl-cta:hover {
          background: #ffffff;
          color: var(--cta-bg, var(--vibe-color-secondary));
          border-color: #ffffff;
          transform: translateY(-2px);
        }

        @media (max-width: 639px) {
          .bbcl-section { padding: 4.5rem 1.25rem 4.5rem; }
          .bbcl-cta { width: 100%; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'BetterBuiltClosing',
  displayName: 'Better-Built Living — Closing CTA',
  tokens: {
    'betterbuiltclosing.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Redefining Modern Living</p>',
    },
    'betterbuiltclosing.subheading': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_name"></span>, we&rsquo;re combining innovation, sustainability, and thoughtful design to transform the way you live. More than just a home, it&rsquo;s a space crafted to enhance your lifestyle. That&rsquo;s what we call &ldquo;Better-Built Living&rdquo;.</p><p>For more information on how we build, visit our manufacturing page.</p>',
    },
  },
  slotGroups: {
    'betterbuiltclosing.background': backgroundDef,
    'betterbuiltclosing.cta': ctaDef,
  },
};

(BetterBuiltClosing as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default BetterBuiltClosing;
